// Pure math and wording for the Deal Sizer panel. No DOM, no fetch, so
// `node --test` covers it. Every number the panel shows is either a value the
// pricer returned or a sum of returned values; nothing here invents a rate,
// a cap, or a fee. The panel code in quote-sizer.js only renders what these
// functions hand back.

const ITEMIZED_KEYS = ['down_payment', 'origination_fee', 'underwriting_fee'];

function num(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function round2(n) {
  return Math.round(n * 100) / 100;
}

export function formatCurrency(n) {
  return '$' + Math.round(Number(n) || 0).toLocaleString('en-US');
}

export function formatPercent(n) {
  return Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + '%';
}

export function formatPoints(n) {
  const points = Number(n);
  return points.toLocaleString('en-US', { maximumFractionDigits: 2 }) + (points === 1 ? ' pt' : ' pts');
}

// One pricer response, flattened. `request` records what was asked for so a
// card can say whether it is the pricer's maximum or a requested structure.
export function normalizeOffer(data, request) {
  const source = data || {};
  return {
    source: 'api',
    requested_ltp: request && request.ltp != null ? Number(request.ltp) : null,
    interest_rate: num(source.interest_rate),
    loan_amount: num(source.loan_amount),
    cash_to_close: num(source.cash_to_close),
    origination_points: num(source.origination_points),
    ltc: num(source.ltc),
    ltarv: num(source.ltarv),
    ltp: num(source.ltp),
    term_months: num(source.term_months),
    limiting_factor: source.limiting_factor || null,
    down_payment: num(source.down_payment),
    origination_fee: num(source.origination_fee),
    underwriting_fee: num(source.underwriting_fee),
    third_party_costs: num(source.third_party_costs),
    out_of_pocket_rehab: num(source.out_of_pocket_rehab)
  };
}

// The cards to show, up to three: 20% down, 10% down, and 0% down when the
// pricer's maximum reaches it, else 5% down. `byLtp` holds the answers to
// the requested structures keyed by ltp (80, 90, 95); any may be missing.
// A request above the deal's cap lands on the same loan as another card and
// must not render twice, so cards dedupe on loan amount, and the pricer's
// maximum wins a tie. More cash down goes first.
export const CARD_LTPS = [80, 90, 95];

export function offerSet(primary, byLtp, inputs) {
  if (!primary || primary.loan_amount === null) return [];
  const asked = byLtp || {};
  const lowDown = downPaymentPercent(primary, inputs) === 0 ? primary : (asked[95] || primary);
  const cards = [];
  function sameLoan(a, b) { return Math.round(a.loan_amount) === Math.round(b.loan_amount); }
  [asked[80], asked[90], lowDown].forEach(function(offer) {
    if (offer && offer.loan_amount !== null && !cards.some(function(c) { return sameLoan(c, offer); })) cards.push(offer);
  });
  const tie = cards.findIndex(function(c) { return sameLoan(c, primary); });
  if (tie !== -1) cards[tie] = primary;
  cards.sort(function(a, b) {
    return (b.cash_to_close === null ? -Infinity : b.cash_to_close) - (a.cash_to_close === null ? -Infinity : a.cash_to_close);
  });
  return cards;
}

// Rehab the pricer funds, from returned values. The out-of-pocket figure is
// what the pricer left with the borrower. When it did not itemize, the
// split follows the pricer's own rule: the purchase advance (ltp of the
// purchase price) takes room under the loan first, the rehab holdback takes
// what is left.
export function rehabFunded(offer, inputs) {
  const rehab = num(inputs && inputs.renovation_budget) || 0;
  if (offer.out_of_pocket_rehab !== null) return Math.max(0, rehab - offer.out_of_pocket_rehab);
  if (offer.loan_amount === null) return null;
  const purchase = num(inputs && inputs.purchase_price) || 0;
  const advance = offer.ltp !== null
    ? Math.min(offer.loan_amount, purchase * offer.ltp / 100)
    : Math.max(0, offer.loan_amount - rehab);
  return Math.max(0, Math.min(rehab, offer.loan_amount - advance));
}

// Cash to close, itemized. Uses the pricer's own lines when it returned
// them. Otherwise the lines are recovered from the totals the pricer did
// return; the last line is whatever the pricer charged beyond down payment,
// points, and out-of-pocket rehab, which on the current pricer is the
// underwriting fee. Returns null when the total cannot be reconciled.
export function cashToCloseItems(offer, inputs) {
  if (offer.cash_to_close === null || offer.loan_amount === null) return null;
  const purchase = num(inputs && inputs.purchase_price) || 0;
  const rehab = num(inputs && inputs.renovation_budget) || 0;
  const refinance = inputs && inputs.purchase_or_refi === 'refinance';
  const downLabel = refinance ? 'Equity gap' : 'Down payment';
  const pointsLabel = offer.origination_points !== null
    ? 'Origination, ' + formatPoints(offer.origination_points)
    : 'Origination';

  let items;
  if (ITEMIZED_KEYS.every(function(key) { return offer[key] !== null; })) {
    items = [
      { key: 'down_payment', label: downLabel, amount: offer.down_payment },
      { key: 'origination_fee', label: pointsLabel, amount: offer.origination_fee },
      { key: 'underwriting_fee', label: 'Underwriting fee', amount: offer.underwriting_fee },
      { key: 'third_party_costs', label: 'Third-party costs', amount: offer.third_party_costs || 0 },
      { key: 'out_of_pocket_rehab', label: 'Rehab you fund', amount: offer.out_of_pocket_rehab || 0 }
    ];
  } else {
    if (offer.origination_points === null) return null;
    const funded = rehabFunded(offer, inputs);
    const advance = offer.loan_amount - funded;
    const down = purchase - advance;
    const points = round2(offer.loan_amount * offer.origination_points / 100);
    const oop = Math.max(0, rehab - funded);
    const fee = round2(offer.cash_to_close - down - points - oop);
    items = [
      { key: 'down_payment', label: downLabel, amount: down },
      { key: 'origination_fee', label: pointsLabel, amount: points },
      { key: 'underwriting_fee', label: 'Underwriting fee', amount: fee },
      { key: 'out_of_pocket_rehab', label: 'Rehab you fund', amount: oop }
    ];
  }

  items = items.filter(function(item) { return item.amount !== 0 || item.key === 'down_payment'; });
  const sum = items.reduce(function(acc, item) { return acc + item.amount; }, 0);
  if (Math.abs(sum - offer.cash_to_close) > 0.01) return null;
  return { items: items, total: offer.cash_to_close };
}

// The panel title is a finding, composed from returned values.
export function panelTitle(offer) {
  if (offer.cash_to_close !== null && offer.loan_amount !== null) {
    return 'You bring ' + formatCurrency(offer.cash_to_close) + ' to close on a ' + formatCurrency(offer.loan_amount) + ' loan.';
  }
  if (offer.loan_amount !== null) return 'Flipmax can lend ' + formatCurrency(offer.loan_amount) + ' on this deal.';
  return null;
}

// Nearest $500, for figures shown as approximate.
export function roundToStep(n, step) {
  return Math.round(Number(n) / step) * step;
}

// Share of the purchase price the borrower puts down, from the pricer's own
// down payment line when it itemized, else purchase less the purchase
// advance (ltp of price, no more than the loan). Null on a refinance or
// when the pricer gave nothing to work from.
export function downPaymentPercent(offer, inputs) {
  const purchase = num(inputs && inputs.purchase_price) || 0;
  if (purchase <= 0 || (inputs && inputs.purchase_or_refi === 'refinance')) return null;
  let down = offer.down_payment;
  if (down === null) {
    if (offer.ltp === null || offer.loan_amount === null) return null;
    down = purchase - Math.min(offer.loan_amount, purchase * offer.ltp / 100);
  }
  return Math.round(down / purchase * 1000) / 10;
}

// Structure card, top to bottom: loan-to-value on the purchase (100 less
// the down payment share), the down payment, closing costs (points, fees,
// and any third-party costs the pricer itemized), rehab the borrower funds
// when there is any, the cash to close as an approximate figure, and the
// monthly payment. Rows the pricer gave nothing for are left out.
export function cardText(offer, inputs) {
  const down = downPaymentPercent(offer, inputs);
  const card = {
    heading: down === null ? null : formatPercent(round2(100 - down)) + ' LTV',
    rows: [],
    total: null,
    payment: null
  };
  const breakdown = cashToCloseItems(offer, inputs);
  // A line recovered from totals that comes out negative means the totals
  // did not follow the pricer's usual rule; show the total alone.
  const sane = breakdown && breakdown.items.every(function(item) { return item.amount >= 0; });
  if (sane) {
    let closing = 0;
    let oop = 0;
    let downAmount = null;
    breakdown.items.forEach(function(item) {
      if (item.key === 'down_payment') downAmount = item.amount;
      else if (item.key === 'out_of_pocket_rehab') oop = item.amount;
      else closing += item.amount;
    });
    card.rows.push({ label: 'Down payment', value: formatCurrency(downAmount) });
    card.rows.push({ label: 'Approx. closing costs', value: formatCurrency(closing) });
    if (oop > 0) card.rows.push({ label: 'Rehab you fund', value: formatCurrency(oop) });
  }
  if (offer.cash_to_close !== null) card.total = 'About ' + formatCurrency(roundToStep(offer.cash_to_close, 500));
  const payment = monthlyPayment(offer, inputs);
  if (payment !== null) card.payment = formatCurrency(payment);
  if (!card.rows.length && card.total === null) card.total = formatCurrency(offer.loan_amount) + ' loan';
  return card;
}

// Interest-only payment on the initial advance: the loan less the rehab
// holdback, which funds later in draws. Null without a rate or a loan.
export function monthlyPayment(offer, inputs) {
  if (offer.interest_rate === null || offer.loan_amount === null) return null;
  const funded = rehabFunded(offer, inputs);
  const advance = offer.loan_amount - (funded || 0);
  return Math.round(advance * offer.interest_rate / 100 / 12);
}

// "11% and 1.5 pts", one line.
export function ratePointsText(offer) {
  const parts = [];
  if (offer.interest_rate !== null) parts.push(formatPercent(offer.interest_rate));
  if (offer.origination_points !== null) parts.push(formatPoints(offer.origination_points));
  return parts.length ? parts.join(' and ') : null;
}

// "All $770,000" or "$37,500 of $50,000"; null when there is no rehab.
export function rehabFundedText(offer, inputs) {
  const rehab = num(inputs && inputs.renovation_budget) || 0;
  if (rehab <= 0) return null;
  const funded = rehabFunded(offer, inputs);
  if (funded === null) return null;
  if (funded >= rehab) return 'All ' + formatCurrency(rehab);
  return formatCurrency(funded) + ' of ' + formatCurrency(rehab);
}

// Screen-reader summary of an offer.
export function offerSummary(offer) {
  const parts = [];
  if (offer.cash_to_close !== null) parts.push(formatCurrency(offer.cash_to_close) + ' to close');
  if (offer.loan_amount !== null) parts.push(formatCurrency(offer.loan_amount) + ' loan');
  if (offer.interest_rate !== null) parts.push('at ' + formatPercent(offer.interest_rate));
  return parts.join(', ');
}
